// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Weapons Configuration
 * 
 * Contains all weapon definitions, costs, and availability by location.
 */

export const weaponsConfig = {
  // Maximum weapon slots
  maxWeaponSlots: 4,
  
  // Starting weapon slots unlocked
  startingSlots: 1,
  
  // Weapon slot unlock costs (in credits)
  slotUnlockCosts: {
    slot2: 300,   // Unlock 2nd weapon slot
    slot3: 600,   // Unlock 3rd weapon slot
    slot4: 900    // Unlock 4th weapon slot
  },
  
  // Weapon charge/energy settings
  maxCharge: 100,           // Maximum charge per weapon
  startingCharge: 100,      // Charge when weapon is purchased/equipped
  rechargeRate: 0,          // Passive recharge rate (0 = no auto-recharge)
  
  // Reload costs (at stations) - calculated per charge unit needed
  // Cost = (chargeNeeded) * weaponReloadCostPerCharge from credits section
  reloadDuration: 2000,     // Time to reload one weapon (ms)
  
  // Starting weapon (player begins with this)
  startingWeapon: {
    id: 'BASIC_LASER',
    name: 'BASIC LASER',
    damage: 10,
    cost: 0,
    description: 'Standard issue energy weapon. Reliable but low damage.',
    tier: 0
  },
  
  // Purchasable weapons catalog
  weaponsCatalog: {
    PULSE_CANNON: {
      id: 'PULSE_CANNON',
      name: 'PULSE CANNON',
      damage: 35,
      cost: 400,
      description: 'Rapid-fire energy pulses. Good all-around weapon.',
      tier: 1
    },
    ION_BEAM: {
      id: 'ION_BEAM',
      name: 'ION BEAM',
      damage: 25,
      cost: 500,
      description: 'Ionized particle beam. Bonus damage vs shields.',
      tier: 1,
      specialEffect: 'shieldBonus' // 1.5x damage vs shields
    },
    PLASMA_TORPEDO: {
      id: 'PLASMA_TORPEDO',
      name: 'PLASMA TORPEDO',
      damage: 50,
      cost: 800,
      description: 'Superheated plasma projectile. High damage output.',
      tier: 2
    },
    DISRUPTOR: {
      id: 'DISRUPTOR',
      name: 'DISRUPTOR',
      damage: 40,
      cost: 1000,
      description: 'Molecular disruptor beam. Fast fire rate.',
      tier: 2,
      specialEffect: 'fastFire' // Reduced cooldown
    },
    PHOTON_LANCE: {
      id: 'PHOTON_LANCE',
      name: 'PHOTON LANCE',
      damage: 60,
      cost: 1200,
      description: 'Focused photon beam. Excellent penetration.',
      tier: 3
    },
    QUANTUM_MISSILE: {
      id: 'QUANTUM_MISSILE',
      name: 'QUANTUM MISSILE',
      damage: 75,
      cost: 2000,
      description: 'Quantum-tipped warhead. Devastating damage.',
      tier: 3,
      specialEffect: 'armorPiercing' // Ignores some armor
    },
    GRAVITON_BEAM: {
      id: 'GRAVITON_BEAM',
      name: 'GRAVITON BEAM',
      damage: 45,
      cost: 1400,
      description: 'Gravity wave projector. Disrupts enemy systems.',
      tier: 2,
      specialEffect: 'systemDisrupt' // Chance to disable enemy weapons
    },
    ANTIMATTER_CANNON: {
      id: 'ANTIMATTER_CANNON',
      name: 'ANTIMATTER CANNON',
      damage: 90,
      cost: 3000,
      description: 'Antimatter annihilation weapon. Extreme damage.',
      tier: 4,
      specialEffect: 'armorPiercing'
    },
    PHASE_DISRUPTOR: {
      id: 'PHASE_DISRUPTOR',
      name: 'PHASE DISRUPTOR',
      damage: 55,
      cost: 1600,
      description: 'Phased energy weapon. Bypasses some defenses.',
      tier: 3,
      specialEffect: 'phaseShift' // Partial shield bypass
    },
    NOVA_LANCE: {
      id: 'NOVA_LANCE',
      name: 'NOVA LANCE',
      damage: 80,
      cost: 2500,
      description: 'Stellar plasma lance. Superheated destruction.',
      tier: 4
    },
    EMP_PROJECTOR: {
      id: 'EMP_PROJECTOR',
      name: 'EMP PROJECTOR',
      damage: 20,
      cost: 1100,
      description: 'Electromagnetic pulse. Disables shields and systems.',
      tier: 2,
      specialEffect: 'empEffect' // Disables shields temporarily
    },
    SINGULARITY_TORPEDO: {
      id: 'SINGULARITY_TORPEDO',
      name: 'SINGULARITY TORPEDO',
      damage: 100,
      cost: 5000,
      description: 'Micro black hole warhead. Ultimate weapon. Legendary.',
      tier: 5,
      specialEffect: 'armorPiercing'
    }
  },
  
  // Weapon availability by location
  // Determines which weapons are available at each specific location
  weaponInventory: {
    // Stations - Each has unique inventory
    'WAYPOINT STATION ALPHA': ['PULSE_CANNON', 'ION_BEAM'],
    'DEEP SPACE STATION MIDWAY': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR', 'GRAVITON_BEAM'],
    'TRADING POST NEXUS': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'QUANTUM_MISSILE', 'GRAVITON_BEAM', 'PHASE_DISRUPTOR', 'EMP_PROJECTOR'],
    'FRONTIER OUTPOST': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'QUANTUM_MISSILE', 'NOVA_LANCE', 'ANTIMATTER_CANNON'],
    'RESEARCH STATION GAMMA': ['PULSE_CANNON', 'ION_BEAM', 'GRAVITON_BEAM', 'PHASE_DISRUPTOR', 'EMP_PROJECTOR'],
    'MILITARY STATION OMEGA': ['PLASMA_TORPEDO', 'PHOTON_LANCE', 'QUANTUM_MISSILE', 'ANTIMATTER_CANNON', 'NOVA_LANCE', 'PHASE_DISRUPTOR'],
    'THE VOID': ['QUANTUM_MISSILE', 'ANTIMATTER_CANNON', 'NOVA_LANCE', 'SINGULARITY_TORPEDO'], // Legendary weapons
    
    // Planets - Basic to medium weapons
    'PROXIMA CENTAURI': ['PULSE_CANNON'],
    'BARNARD\'S STAR': ['PULSE_CANNON'],
    'LUYTEN\'S STAR': ['PULSE_CANNON', 'ION_BEAM'],
    'WOLF 359': ['PULSE_CANNON'],
    'SIRIUS': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'EPSILON ERIDANI': ['PULSE_CANNON', 'ION_BEAM'],
    'TAU CETI': ['PULSE_CANNON', 'ION_BEAM'],
    'PROCYON': ['PULSE_CANNON', 'ION_BEAM'],
    'ROSS 154': ['PULSE_CANNON'],
    '51 PEGASI': ['PULSE_CANNON', 'ION_BEAM'],
    'GLIESE 667C': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'TRAPPIST-1': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'HD 110067': ['PULSE_CANNON', 'ION_BEAM'],
    'EPSILON DRACONIS': ['PULSE_CANNON'],
    'POLARIS': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR'],
    'BETELGEUSE': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'RIGEL': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'PHOTON_LANCE'],
    'VEGA': ['PULSE_CANNON', 'ION_BEAM'],
    'ALTAIR': ['PULSE_CANNON'],
    'FOMALHAUT': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'DENEB': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'PHOTON_LANCE'],
    'ARCTURUS': ['PULSE_CANNON', 'ION_BEAM'],
    'CAPELLA': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'ALDEBARAN': ['PULSE_CANNON', 'ION_BEAM'],
    'ANTARES': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'QUANTUM_MISSILE'],
    'SPICA': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'REGULUS': ['PULSE_CANNON', 'ION_BEAM'],
    'CANOPUS': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR'],
    'ACHERNAR': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'HADAR': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'PHOTON_LANCE'],
    
    // DISCWORLD UNIVERSE - Planets
    'DISCWORLD': ['PULSE_CANNON', 'ION_BEAM'],
    'ROUNDWORLD': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'XXXX': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'KLATCH': ['PULSE_CANNON', 'ION_BEAM'],
    'FOURECKS MOON': ['PULSE_CANNON', 'ION_BEAM'],
    'LLAMEDOS': ['PULSE_CANNON'],
    'LANCRE': ['PULSE_CANNON'],
    'RAMTOPS': ['PULSE_CANNON', 'ION_BEAM'],
    'BAD ASS': ['PULSE_CANNON'],
    'ÜBERWALD': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'GENUA': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'EPHEBE': ['PULSE_CANNON', 'ION_BEAM'],
    'OMNIA': ['PULSE_CANNON', 'ION_BEAM'],
    'TSORT': ['PULSE_CANNON'],
    'DJELIBEYBI': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'COUNTERWEIGHT CONTINENT': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR'],
    'HUNGHUNG': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR'],
    'BES PELARGIC': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'QUIRM': ['PULSE_CANNON', 'ION_BEAM'],
    'KRULL': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'WYRMBERG': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'SKUND FOREST': ['PULSE_CANNON'],
    'DUNGEON DIMENSIONS': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE'],
    
    // DISCWORLD UNIVERSE - Stations
    'UNSEEN UNIVERSITY OBSERVATORY': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR', 'GRAVITON_BEAM', 'PHASE_DISRUPTOR'],
    'PATRICIAN\'S ORBITAL PLATFORM': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'QUANTUM_MISSILE'],
    'IGOR\'S SPARE PARTS DEPOT': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'DEATH\'S DOMAIN OUTPOST': ['PLASMA_TORPEDO', 'PHOTON_LANCE', 'QUANTUM_MISSILE', 'NOVA_LANCE'],
    'COHEN\'S BARBARIAN OUTPOST': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'QUANTUM_MISSILE'],
    
    // IMPROBABILITY UNIVERSE - Planets
    'MAGRATHEA': ['PULSE_CANNON', 'ION_BEAM'],
    'BETELGEUSE SEVEN': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'FROGSTAR WORLD B': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'MILLIWAYS': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR'],
    'DAMOGRAN': ['PULSE_CANNON'],
    'KAKRAFOON': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'URSA MINOR BETA': ['PULSE_CANNON', 'ION_BEAM'],
    'SQORNSHELLOUS ZETA': ['PULSE_CANNON'],
    'PRELIUMTARN': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE'],
    'BARTLEDAN': ['PULSE_CANNON', 'ION_BEAM'],
    
    // IMPROBABILITY UNIVERSE - Stations
    'DEEP THOUGHT STATION': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'GRAVITON_BEAM', 'PHASE_DISRUPTOR'],
    'HEART OF GOLD DEPOT': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'QUANTUM_MISSILE', 'ANTIMATTER_CANNON'],
    'SIRIUS CYBERNETICS OUTPOST': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'GOLGAFRINCHAN ARK STATION': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'QUANTUM_MISSILE'],
    
    // DUNGEON DIMENSIONS UNIVERSE - Planets
    'REALITY ANCHOR': ['PULSE_CANNON', 'ION_BEAM'],
    'THE SCREAMING VOID': ['PULSE_CANNON', 'ION_BEAM'],
    'IMPOSSIBLE ANGLES': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'THINGS GATHERING PLACE': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE'],
    'MATHEMATICS BREAKDOWN ZONE': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'THE COLOUR OF MAGIC': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR'],
    'PROBABILITY STORM': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'THE EIGHT FOLD PATH': ['PULSE_CANNON', 'ION_BEAM'],
    'REALITY FRACTURE POINT': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'QUANTUM_MISSILE'],
    'THE LUGGAGE GRAVEYARD': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'THE UNSPACE': ['PULSE_CANNON', 'ION_BEAM'],
    'DUNGEON DIMENSIONS NEXUS': ['PLASMA_TORPEDO', 'PHOTON_LANCE', 'QUANTUM_MISSILE', 'ANTIMATTER_CANNON', 'NOVA_LANCE'],
    'THE LAST THEOREM': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR'],
    'THE THING THAT SHOULD NOT BE': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE'],
    'THE EDGE OF REASON': ['PLASMA_TORPEDO', 'PHOTON_LANCE', 'QUANTUM_MISSILE', 'ANTIMATTER_CANNON'],
    
    // L-SPACE UNIVERSE - Will be added when systems are defined
    'THE GREAT LIBRARY': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'LIBRARY OF ALEXANDRIA': ['PULSE_CANNON', 'ION_BEAM'],
    'LIBRARY OF BABEL': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'AKASHIC RECORDS': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR'],
    'FORBIDDEN SECTION': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE'],
    'INDEX INFINITUS': ['PULSE_CANNON', 'ION_BEAM'],
    'READING ROOM OMEGA': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    'CATALOGUE NEXUS': ['PULSE_CANNON', 'ION_BEAM'],
    'REFERENCE DESK PRIME': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'THE STACKS': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO'],
    
    // L-SPACE UNIVERSE - Stations
    'LIBRARIAN\'S SANCTUARY': ['PULSE_CANNON', 'ION_BEAM', 'PLASMA_TORPEDO', 'DISRUPTOR', 'GRAVITON_BEAM'],
    'BOOK RETURN STATION': ['PULSE_CANNON', 'ION_BEAM', 'DISRUPTOR'],
    'OVERDUE COLLECTION POINT': ['PLASMA_TORPEDO', 'DISRUPTOR', 'PHOTON_LANCE', 'QUANTUM_MISSILE']
  },
  
  // Weapon status states
  statusStates: {
    READY: 'READY',           // Weapon ready to fire
    ARMED: 'ARMED',           // Weapon armed and ready
    CHARGING: 'CHARGING',     // Weapon charging up
    RECHARGING: 'RECHARGING', // Weapon recharging after fire
    DEPLETED: 'DEPLETED',     // Weapon out of charge
    OFFLINE: 'OFFLINE'        // Weapon offline/damaged
  },
  
  // Default weapon charge thresholds for status
  chargeThresholds: {
    ready: 80,      // Above 80% = READY
    recharging: 20, // 20-80% = RECHARGING
    depleted: 0     // 0% = DEPLETED
  }
};
